import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import { FormConfig } from '../../components/Fields/Types';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import MilestoneDefinitionModel from '../../models/MilestoneDefinitionModel';
import DataService from '../../services/data.service';
import { Result } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import MilestoneDefinitionControl from './Controls/MilestoneDefinitionControl';

const MilestoneDefinitionView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const [formData, setFormData] = useState<MilestoneDefinitionModel>();

    const milestoneDefinitionId = useParams().id;

    useEffect(() => {
        if (milestoneDefinitionId !== undefined) {
            DataService.getById<MilestoneDefinitionModel>('api/v1/milestoneDefinitions/', milestoneDefinitionId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [milestoneDefinitionId]);

    function submit() {
        if (formData) {
            DataService
                .update<MilestoneDefinitionModel>(`api/v1/milestoneDefinitions/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'projectTypeId', label: 'Project type', labelSize:3, validationRules: [{ type: 'isRequired' }] },
            { name: 'name', label: 'Name', labelSize: 3, validationRules: [{ type: 'isRequired' }] },
            { name: 'milestoneTypeId', label: 'Type', labelSize: 3, validationRules: [{ type: 'isRequired' }] },
            { name: 'attachmentTypeId', label: 'Attachment type to achieve milestone', labelSize: 3 },
            { name: 'daysToAchieve', label: 'Days to achieve', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isNumber' }] },
            { name: 'lateDays', label: 'Days', labelSize: 'auto', validationRules: [{ type: 'isRequiredNumber' }, { type: 'isNumber' }] },
            { name: 'lateHours', label: 'Hours', labelSize: 'auto', validationRules: [{ type: 'isNumber' }, { type: 'isMinValue', minValue:-3.5 }] },
            { name: 'lateMinutes', label: 'Minutes after milestone date', labelSize: 'auto', validationRules: [{ type: 'isNumber' }] },
            { name: 'missedDays', label: 'Days', labelSize: 'auto', validationRules: [{ type: 'isRequiredNumber' }, { type: 'isNumber' }] },
            { name: 'missedHours', label: 'Hours', labelSize: 'auto', validationRules: [{ type: 'isNumber' }] },
            { name: 'missedMinutes', label: 'Minutes after milestone date', labelSize: 'auto', validationRules: [{ type: 'isNumber' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (milestoneDefinitionId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.ViewMilestoneDefintions)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    if (!formData)
        return <SpinnerOverlay />

    return (
        <div className='main-content'>
            <Form title='Milestone' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={3}>
                            <Authorize applicationFunction={ApplicationFunctions.EditMilestoneDefintions}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <MilestoneDefinitionControl getFieldProps={getFieldProps} getControlProps={getControlProps} getLabelProps={getLabelProps} formData={formData}  />
            </Form>
        </div>
    );
}
export default MilestoneDefinitionView;